import axios from 'axios';
import type { Project, Scenario, CreateProjectDto, CreateScenarioDto } from './types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Cliente API para proyectos
 */
export const projectsApi = {
  /**
   * Listar proyectos
   */
  async list(): Promise<Project[]> {
    const token = getAuthToken();
    const response = await axios.get<Project[]>(`${API_URL}/projects`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Obtener proyecto por ID
   */
  async get(id: string): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.get<Project>(`${API_URL}/projects/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Crear proyecto
   */
  async create(data: CreateProjectDto): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.post<Project>(`${API_URL}/projects`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Actualizar proyecto
   */
  async update(id: string, data: Partial<CreateProjectDto>): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.put<Project>(`${API_URL}/projects/${id}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Eliminar proyecto
   */
  async delete(id: string): Promise<void> {
    const token = getAuthToken();
    await axios.delete(`${API_URL}/projects/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
};

/**
 * Cliente API para escenarios
 */
export const scenariosApi = {
  /**
   * Listar escenarios de un proyecto
   */
  async list(projectId: string): Promise<Scenario[]> {
    const token = getAuthToken();
    const response = await axios.get<Scenario[]>(`${API_URL}/projects/${projectId}/scenarios`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Crear escenario
   */
  async create(projectId: string, data: CreateScenarioDto): Promise<Scenario> {
    const token = getAuthToken();
    const response = await axios.post<Scenario>(
      `${API_URL}/scenarios`,
      { ...data, projectId },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Publicar escenario
   */
  async publish(id: string): Promise<Scenario> {
    const token = getAuthToken();
    const response = await axios.post<Scenario>(
      `${API_URL}/scenarios/${id}/publish`,
      {},
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Duplicar escenario
   */
  async duplicate(id: string): Promise<Scenario> {
    const token = getAuthToken();
    const response = await axios.post<Scenario>(
      `${API_URL}/scenarios/${id}/duplicate`,
      {},
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
};
